/* What the decide card can put side by side (Tier-2 D1) — read out of
 * the deck payload, never derived. The two shapes the deck serves for
 * the same question are handled here so both card renderers show the
 * officer the same comparison:
 *   · the v13 claims card — a NEW claim (the document's proposal, with
 *     its source chip) and one ON RECORD claim per candidate;
 *   · the legacy card — the question block alone, whose candidates are
 *     bags of record fields.
 */

import type { Claim, ClaimChip, DeckCard } from './types'

export interface CandidateSide {
  /** The object's name, as its side words it. */
  name: string
  /** NEW = the document's proposal · ON RECORD = what the record holds. */
  state: 'new' | 'on_record'
  /** The source chip, when this side came from a document. */
  chip?: ClaimChip | null
  /** The one-line detail the side states about itself. */
  stated?: string | null
  /** Figures this side states, label / value, straight from the payload. */
  figures: [string, string][]
}

/** A candidate is a bag of record fields: the names and ids are its
 *  identity, everything else is a figure the officer can compare. */
const ID_KEYS = ['name', 'canonical_name', 'entity_id', 'instrument_id', 'proposal_id', 'id']

export function candidateFigures(c: Record<string, unknown>): [string, string][] {
  return Object.entries(c ?? {})
    .filter(([k, v]) => !ID_KEYS.includes(k) && v !== null && v !== undefined && v !== '')
    .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
    .map(([k, v]) => [k.replace(/_/g, ' '), String(v)] as [string, string])
}

const nameOfCandidate = (c: Record<string, unknown> | undefined): string | null =>
  (c?.name as string | undefined) ?? (c?.canonical_name as string | undefined) ?? null

/** A claim's sentence: the bold span is the name, the rest is what the
 *  side states about itself. */
function claimName(c: Claim): string {
  return c.parts.find((p) => p.bold)?.text?.trim() ?? c.parts[0]?.text?.trim() ?? 'object'
}
function claimStated(c: Claim): string | null {
  const rest = c.parts
    .filter((p) => !p.bold)
    .map((p) => p.text)
    .join('')
    .replace(/^\s*—\s*/, '')
    .trim()
  return rest || null
}

/** The two sides of the identity question — the document's object
 *  first, then the record candidate the merge currently targets. */
export function decideSides(card: DeckCard, targetIdx: number): CandidateSide[] {
  const q = card.question
  if (!q) return []
  const candidates = (q.candidates ?? []) as Record<string, unknown>[]
  if (candidates.length === 0) return []
  const target = candidates[Math.min(targetIdx, candidates.length - 1)]
  const targetName = nameOfCandidate(target)

  const claims = card.claims ?? []
  const proposed = claims.find((c) => c.state === 'new' || c.state === 'update')
  const onRecord = claims.filter((c) => c.state === 'on_record')
  const recordClaim =
    onRecord.find((c) => targetName && claimName(c) === targetName) ??
    (onRecord.length === 1 ? onRecord[0] : undefined)

  const documentSide: CandidateSide = {
    name: proposed ? claimName(proposed) : q.label,
    state: 'new',
    chip: (proposed?.chips ?? [])[0] ?? null,
    stated: proposed ? claimStated(proposed) : null,
    figures: [],
  }
  const recordSide: CandidateSide = {
    name: targetName ?? (recordClaim ? claimName(recordClaim) : 'the record'),
    state: 'on_record',
    chip: (recordClaim?.chips ?? [])[0] ?? null,
    stated: recordClaim ? claimStated(recordClaim) : null,
    figures: candidateFigures(target),
  }
  return [documentSide, recordSide]
}
